/**
 * Anthropic server-side code execution tool (`code_execution_20260120`).
 *
 * Claude writes Python/bash and runs it in a sandboxed container operated by
 * Anthropic — NOT by us. There is no client-side executor: the tool_use and its
 * result both arrive inline in the same assistant message, carrying
 * `providerExecuted: true` and `srvtoolu_…` ids. That is the same shape as
 * `tool_search`, so the existing server-tool handling in
 * `mergeToolMessagesIntoAssistant` (see `isProviderExecutedToolPart`) already
 * keeps these blocks inline instead of re-emitting them as client tool_results
 * (which Anthropic rejects with HTTP 400).
 *
 * WHY IT'S AVAILABLE HERE: Meridian runs on Claude Platform on AWS
 * (`@ai-sdk/anthropic-aws`), which is Anthropic-operated with same-day API
 * parity — code execution is GA there. It is NOT available on Amazon Bedrock,
 * and DeepSeek has no equivalent, so this resolves to `null` on any non-AWS
 * provider (see `../../providers`).
 *
 * SECURITY POSTURE (deliberate, reviewed):
 *   - The container has NO internet access and cannot reach our VPC, our
 *     database, or our S3. A prompt injection cannot exfiltrate through it.
 *   - There is NO approval gate. Server tools have no `execute` for the HITL
 *     approval executor to wrap, so code runs unreviewed. This is why the tool
 *     is env-gated rather than on by default.
 *   - Container data (artifacts, uploaded files, outputs) is retained by
 *     Anthropic for up to 30 days. Whatever CRM data Claude writes to a file
 *     lives there for that window. Same trust boundary as the prompt itself,
 *     but a retention surface we do not otherwise have.
 *
 * COST: billed by execution time with a 5-minute MINIMUM per execution; 1,550
 * free container-hours per month per organization, then $0.05/hour. The 5-minute
 * floor means cost tracks the NUMBER of executions, not compute time —
 * ~18,600 executions/month fit inside the free tier. Note the documented trap:
 * when a request carries files, execution time is billed even if the tool is
 * never called, because files are preloaded onto the container.
 *
 * FREE PATH (not taken today): code execution is free when the request also
 * includes Anthropic's server-side `web_search_20260209` / `web_fetch_20260209`
 * or later. Meridian uses its own Serper-backed `web_search` tool instead, so
 * standard pricing applies. Revisit if web search ever moves server-side.
 */

import type { ResolvedLlmProvider } from '../../providers/index.js';
import { logCapabilitySkip, supportsCodeExecution } from '../../models/capabilities.js';
import { logger } from '../../logger.js';

/**
 * Key the tool is registered under in the AI SDK tools record. Must match the
 * `name` Anthropic expects for the provider-defined tool.
 */
export const CODE_EXECUTION_TOOL_NAME = 'code_execution';

/**
 * Feature flag. Off unless explicitly enabled, because the tool cannot be
 * approval-gated (see SECURITY POSTURE above) — enabling it is a deliberate
 * decision, not a default.
 */
export function isCodeExecutionEnabled(): boolean {
  return process.env.MERIDIAN_CODE_EXECUTION === 'true';
}

/**
 * Resolve the code-execution server tool for the active provider + MODEL, or
 * `null` when it is unavailable (non-AWS provider, or a model that does not
 * support it) or disabled (flag off).
 *
 * `modelId` is required because provider kind alone is not the gate — see
 * `../../models/capabilities`, written after a Haiku 4.5 turn was rejected
 * outright by the API. An omitted/unknown id resolves to "no capability", which
 * costs the feature but never kills the turn.
 *
 * Returns `unknown` rather than a concrete tool type for the same reason the
 * surrounding tool records are `Record<string, unknown>`: the AI SDK's
 * provider-defined tool type is not structurally compatible with the function
 * tools in the same map, and widening at the boundary keeps callers from
 * needing an assertion.
 */
export function resolveCodeExecutionServerTool(
  resolved: ResolvedLlmProvider,
  modelId: string | null | undefined,
  logContext?: Record<string, unknown>,
): unknown | null {
  if (!isCodeExecutionEnabled()) return null;

  if (resolved.providerKind !== 'aws') {
    // Not an error: DeepSeek genuinely has no server-side code execution. Warn
    // so a flag left on after a provider flip is visible instead of silent.
    logger.warn('code execution requested on a provider that does not support it', {
      ...logContext,
      op: 'resolveCodeExecutionServerTool',
      providerKind: resolved.providerKind,
    });
    return null;
  }

  if (!supportsCodeExecution(modelId)) {
    // A Claude model this deployment can serve but whose compatibility we have
    // not recorded. Info, not warn: the turn is fine, the feature is simply not
    // offered here. Add the id to `MODEL_CAPABILITIES` to turn it on.
    logCapabilitySkip('code_execution', modelId, logContext);
    return null;
  }

  return resolved.provider.tools.codeExecution_20260120({});
}
